import "../../css/KlaviyoConfiguration.css";
import React, { useState, useEffect, useCallback } from "react";
import { Button, Tabs, Spin, message } from "antd";
import backArrow from "../../assets/Images/backArrow.svg";
import ActivateLogo from "../../assets/Images/ActivateLogo.svg";
import {
  getSubscription,
  getSubscriptionSummary,
  getOAuthAuthorizeUrl,
} from "../../services/integrations/klaviyoSubscriptionService";
import { getDemoStatus, resetDemoSandbox, getWebchatLink, getStoreConfig } from "../../services/polly/demoService";
import EventMappingsTab from "./klaviyo/EventMappingsTab";
import CatalogSyncTab from "./klaviyo/CatalogSyncTab";
import SettingsTab from "./klaviyo/SettingsTab";

export default function KlaviyoConfiguration({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isDemoTenant, setIsDemoTenant] = useState(false);
  const [lastResetAt, setLastResetAt] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [activeTab, setActiveTab] = useState("mappings");

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSubscription();
      setSubscription(res);
      try {
        const sum = await getSubscriptionSummary();
        setSummary(sum);
      } catch (e) {
        // summary may not be available yet
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setSubscription(null);
      } else {
        message.error("Failed to load Klaviyo subscription");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Check for OAuth callback result
    const params = new URLSearchParams(window.location.search);
    if (params.get("oauth") === "success") {
      message.success("Klaviyo connected via OAuth");
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("error")) {
      message.error("OAuth connection failed: " + params.get("error"));
      window.history.replaceState({}, "", window.location.pathname);
    }
    fetchSubscription();
    getDemoStatus().then(s => {
      setIsDemoTenant(s?.isDemoTenant === true);
      if (s?.lastResetAt) setLastResetAt(s.lastResetAt);
      if (s?.isDemoTenant) setActiveTab("mappings");
    }).catch(() => {});
  }, [fetchSubscription]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="klaviyo-config-container">
        <Spin spinning={true}><div style={{ minHeight: 200 }} /></Spin>
      </div>
    );
  }

  // Connected state
  if (subscription) {
    const eventCount = summary?.eventMappingsActive ?? 0;
    const catalogCount = summary?.catalogItemCount ?? 0;

    const tabItems = [
      {
        key: "mappings",
        label: "Mappings",
        children: <EventMappingsTab subscriptionId={subscription?.id} />,
      },
      {
        key: "catalog",
        label: "Catalog Sync",
        children: <CatalogSyncTab customerId={subscription?.customerId} />,
      },
      {
        key: "settings",
        label: "Settings",
        children: (
          <SettingsTab
            subscription={subscription}
            onRefresh={fetchSubscription}
            onDisconnect={() => { setSubscription(null); setSummary(null); }}
          />
        ),
      },
    ];

    return (
      <div className="klaviyo-config-container">
        <div className="klaviyo-config-header" style={{ display: "flex", alignItems: "center" }}>
          <img className="back-arrow" src={backArrow} alt="" onClick={onBack} />
          <h2 style={{ margin: 0 }}>Klaviyo Integration</h2>
          <div className="active-badge" style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
            <img src={ActivateLogo} alt="" />
            <span>Active</span>
          </div>
        </div>
        <div className="klaviyo-config-meta">
          Connected: {formatDate(subscription.createdAt)}
          &nbsp;&nbsp;&bull;&nbsp;&nbsp;
          OAuth
          &nbsp;&nbsp;&bull;&nbsp;&nbsp;
          Event mappings: {eventCount} active
          &nbsp;&nbsp;&bull;&nbsp;&nbsp;
          Catalog: {catalogCount} items
        </div>

        {isDemoTenant && (
          <div style={{
            padding: "12px 16px", background: "#f8fafc", borderRadius: 6,
            border: "1px solid #e2e8f0", marginBottom: 16
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
              Sandbox Controls
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Button type="primary" icon={<span role="img" aria-label="chat" style={{ marginRight: 6 }}>💬</span>}
              style={{ padding: "6px 16px", height: "auto" }}
              onClick={async () => {
                try {
                  const data = await getWebchatLink();
                  const base = window.location.hostname === "localhost"
                    ? `http://${data.subdomain}.localhost:3846`
                    : `https://${data.subdomain}.myblustream.io`;
                  window.open(`${base}?magiclink=${data.code}`, "_blank");
                } catch (e) { message.error("Failed to open ChatStream"); }
              }}>Open ChatStream</Button>
            <Button type="primary" icon={<span role="img" aria-label="store" style={{ marginRight: 6 }}>🛒</span>}
              style={{ padding: "6px 16px", height: "auto" }}
              onClick={async () => {
                try {
                  const data = await getStoreConfig();
                  const gandalfBase = process.env.REACT_APP_API_URL
                    ? `${process.env.REACT_APP_API_URL}/gandalfproxy`
                    : "";
                  const params = new URLSearchParams({
                    customerId: data.customerId, profileId: data.profileId,
                    profileEmail: data.profileEmail, productName: data.productName,
                    gandalfBase,
                  });
                  window.open(`/demo/sandbox-store.html?${params}`, "_blank");
                } catch (e) { message.error("Failed to open store"); }
              }}>Open Demo Store</Button>
            <Button loading={resetting}
              style={{ borderColor: "#cbd5e1", color: "#475569", padding: "6px 16px", height: "auto" }}
              icon={<span role="img" aria-label="reset" style={{ marginRight: 6 }}>🔄</span>}
              onClick={async () => {
                setResetting(true);
                try {
                  const result = await resetDemoSandbox();
                  message.success("Sandbox reset complete");
                  if (result?.resetAt) setLastResetAt(result.resetAt);
                  await fetchSubscription();
                } catch (e) { message.error("Reset failed"); }
                setResetting(false);
              }}>{resetting ? "Resetting..." : "Reset Sandbox"}</Button>
            {lastResetAt && (
              <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: 4 }}>
                Last reset: {new Date(lastResetAt.replace(/\[.*\]$/, '')).toLocaleString()}
              </span>
            )}
            </div>
          </div>
        )}

        <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
      </div>
    );
  }

  // Connect form (empty state)
  const handleOAuthConnect = async () => {
    try {
      const url = await getOAuthAuthorizeUrl();
      window.location.href = url;
    } catch (err) {
      message.error("Failed to start OAuth flow");
    }
  };

  return (
    <div className="klaviyo-config-container">
      <div className="klaviyo-config-header">
        <img className="back-arrow" src={backArrow} alt="" onClick={onBack} />
        <h2>Connect Klaviyo</h2>
      </div>
      <div className="klaviyo-connect-form">
        <p className="connect-description">
          Polly conversation events, sentiment, and product mentions
          will be sent to Klaviyo to power your marketing flows.
        </p>

        <Button
          type="primary"
          size="large"
          className="primary-orange-button"
          onClick={handleOAuthConnect}
          style={{ width: "100%", marginBottom: 16 }}
        >
          Connect to Klaviyo
        </Button>
      </div>
    </div>
  );
}
